import { ROUTES } from "@app/constants";
import { useGetBriefFacets } from "@entities/brief/lib/hooks/useGetBriefFacets";
import { useGetBriefs } from "@entities/brief/lib/hooks/useGetBriefs";

import { useGetBrands } from "@entities/brand";
import { CampaignStatus, getStatusMainStatusFilter } from "@entities/brief/lib/utils/statusHelpers";
import BriefForm from "@entities/brief/ui/BriefForm";
import { CampaignTableView } from "@entities/brief/ui/CampaignTableView";
import { isNumber } from "@shared/lib/utils/isValueExists";
import { Button, Modal, Segmented, TableTitleLayout } from "@shared/ui";
import SearchWithFilters from "@widgets/SearchWithFilters";
import { Plus, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDebounce } from "use-debounce";
import styles from "./Campaigns.module.scss";

const CampaignsPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CampaignStatus>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchQuery] = useDebounce(searchQuery, 500);
  const { facets, isFetching: isFetchingFacets } = useGetBriefFacets();

  const [isNewCampaignModalOpen, setIsNewCampaignModalOpen] = useState(false);
  const [pageNo, setPageNo] = useState<number>(1);
  const [pageSize] = useState(10);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);

  const [accountManagersBrandIds, setAccountManagersBrandIds] = useState<
    string[]
  >([]);

  const [brandSearchQuery, setBrandSearchQuery] = useState("");
  const [debouncedBrandSearchQuery] = useDebounce(brandSearchQuery, 500);


  const { briefs, isFetching, totaldata, refetch } = useGetBriefs({
    pageNo: pageNo,
    limit: pageSize,
    prospectBudget: 0,
    confidence: 0,
    status:
      filter === "all"
        ? ["Active", "Pending", "Closed"]
        : filter === "completed"
          ? ["Closed"]
          : ["Active", "Pending"],
    searchQuery: debouncedSearchQuery,
    mainStatus: getStatusMainStatusFilter(filter),
    campaignByBrandIds: selectedBrands,
    accountManagersBrandIds
  });

  const { brands, isFetchingOrig: isFetchingBrands } = useGetBrands(
      10,
      1,
      debouncedBrandSearchQuery,
      undefined,
      undefined,
      undefined,
      undefined,
    );

  useEffect(() => {
    setPageNo(1);
  }, [debouncedSearchQuery, selectedBrands]);

  useEffect(() => {
    refetch();
  }, [filter]);

  const filters = [
    { id: "all" as const, label: "All Campaigns" },
    { id: "brief" as const, label: "In Brief" },
    { id: "execution" as const, label: "In Execution" },
    { id: "completed" as const, label: "Closed" },
  ];

        const brandOptions = (brands?.data || []).map((val) => ({label: val.name, value: val._id, name: val.name}));


  return (
    <div className={styles.campaignsPage}>
      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>Campaigns</h1>
            {/* <p className={styles.subtitle}>
              Manage your campaigns from briefing to execution.
            </p> */}
          </div>
        </div>

        {/* Campaign Table */}
        <CampaignTableView
          briefs={briefs}
          isFetching={isFetching}
          pageNo={pageNo}
          pageSize={pageSize}
          totaldata={totaldata}
          onPageChange={(page) => setPageNo(page)}
          onRowClick={(brief) => {
            if (brief.mainStatus === "Campaign") {
              return navigate(
                ROUTES.BRIEF_V2.replace(":briefId", brief._id) +
                  "?tab=execution",
                {
                  state: { title: brief.name },
                }
              );
            } else {
              if (brief?.totalInfluencerCount > 0) {
                return navigate(
                  ROUTES.BRIEF_V2.replace(":briefId", brief._id) + "?tab=brief",
                  {
                    state: { title: brief.name },
                  }
                );
              } else {
                return navigate(
                  ROUTES.BRIEF_V2.replace(":briefId", brief._id) +
                    "?tab=overview",
                  {
                    state: { title: brief.name },
                  }
                );
              }
            }

            // if (brief.mainStatus === "Brief") {
            //   navigate(ROUTES.BRIEF_V2.replace(":briefId", brief._id), {
            //     state: { title: brief.name },
            //   });
            // } else {
            //   navigate(
            //     // ROUTES.BRIEF_V2.replace(":briefId", brief._id)+?'',
            //     {
            //       state: { title: brief.name },
            //     }
            //   );
            // }
          }}
          title={
            <TableTitleLayout 
              filterComponent={
        <div className={styles.controlsBar}>
          <div className={styles.searchContainer}>
            <SearchWithFilters
              inputProps={{
                placeholder: "Search campaigns...",
                value: searchQuery,
                onChange: (e) => {
                  setSearchQuery(e.target.value);
                  setPageNo(1);
                },
                prefix: <Search size={16} className={styles.searchIcon} />,
                // className: styles.searchInput,
              }}
              selectProps={[
                {
                  searchable: true,
                  mode: "multiple",
                  options: brandOptions,
                  searchValue: brandSearchQuery,
                  onSearchChange: setBrandSearchQuery,
                  isLoading: isFetchingBrands,
                  value: selectedBrands,
                  optionLabelProp: "name",
                  onChange: (brands) => setSelectedBrands(brands as string[]),
                  placeholder: "Search Brands",
                  withFooterActions: true,
                },
              ]}
            />
          </div>
          <div className={styles.filters}>
            <Segmented
              className={styles.filtersSegmented}
              value={filter}
              onChange={(value) => {
                setFilter(value as CampaignStatus);
                setPageNo(1);
              }}
              options={filters.map((item) => {
                const itemCount =
                  item.id === "all"
                    ? facets?.all || 0
                    : item.id === "brief"
                      ? facets?.inBriefs || 0
                      : item.id === "execution"
                        ? facets?.inExecution || 0
                        : facets?.closed || 0;

                return {
                  label: (
                    <div
                      className={`${styles.segmentLabel} ${filter === item.id ? styles.selectedLabel : ""}`}
                    >
                      {item.label}
                      {isNumber(itemCount) && (
                        <span
                          className={`${styles.statusBadge} ${filter === item.id ? styles.selected : ""}`}
                        >
                          {itemCount}
                        </span>
                      )}
                    </div>
                  ),
                  value: item.id,
                };
              })}
            />
          </div>
          <Button
              type="primary"
              onClick={() => setIsNewCampaignModalOpen(true)}
              className={styles.newCampaignButton}
              icon={<Plus size={16} />}
            >
              New Campaign
            </Button>
        </div>
              }
              totalText={`${totaldata} results found`}
            />
            // <div
            //   style={{
            //     padding: "0.75rem 1.5rem",
            //     background: "#fcfdfe",
            //   }}
            // >
            //   <span
            //     className={styles.tableTitle}
            //   >{`${totaldata} results found`}</span>
            // </div>
          }
          onSelectedAccountManagerBrandIdsChange={setAccountManagersBrandIds}
        />
      </div>

      {/* New Campaign Modal */}
      <Modal
        open={isNewCampaignModalOpen}
        onCancel={() => setIsNewCampaignModalOpen(false)}
        title="Create New Campaign"
        footer={null}
        className={styles.newCampaignModal}
        width={480}
        bodyProps={{ style: { padding: "21px" } }}
        centered
        destroyOnClose
      >
        <BriefForm
          closeModal={() => {
            setIsNewCampaignModalOpen(false);
            setSearchQuery("");
            setPageNo(1);
          }}
        />
      </Modal>
    </div>
  );
};

export default CampaignsPage;
